// Scoring engine for the AI Org Health Score assessment
import { DIMENSIONS, QUESTIONS } from './questions';

// Calculate dimension scores from answers
export function calculateDimensionScores(answers) {
  const dimensionScores = DIMENSIONS.map(dimension => {
    const dimensionQuestions = QUESTIONS.filter(q => q.dimensionId === dimension.id);
    
    let rawScore = 0;
    let answeredCount = 0;
    
    dimensionQuestions.forEach(question => {
      const answerIndex = answers[question.id - 1]; // 0-indexed
      if (answerIndex !== undefined && answerIndex !== null) {
        rawScore += question.options[answerIndex].score;
        answeredCount++;
      }
    });
    
    const maxPossible = dimensionQuestions.length * 5;
    const percentage = answeredCount > 0 ? Math.round((rawScore / maxPossible) * 100) : 0;
    
    // Determine tier and color based on percentage
    let tier, color;
    if (percentage < 45) {
      tier = 'critical';
      color = 'red';
    } else if (percentage < 70) {
      tier = 'developing';
      color = 'amber';
    } else {
      tier = 'strong';
      color = 'green';
    }
    
    return {
      dimensionId: dimension.id,
      name: dimension.name,
      icon: dimension.icon,
      rawScore,
      maxPossible,
      percentage,
      tier,
      color,
      description: dimension.description
    };
  });
  
  return dimensionScores;
}

// Calculate overall score (weighted average of dimension percentages)
// This ensures equal weighting regardless of question count per dimension
export function calculateOverallScore(dimensionScores) {
  // Each dimension contributes equally (1/6th) regardless of question count
  const total = dimensionScores.reduce((sum, d) => sum + d.percentage, 0);
  return Math.round(total / dimensionScores.length);
}

// Alternative: Calculate weighted by question importance
export function calculateWeightedOverallScore(dimensionScores) {
  // Weight dimensions by strategic importance
  const weights = {
    strategic: 1.2,  // Strategy alignment is foundational
    process: 1.1,    // Process efficiency has broad impact
    data: 1.0,       // Data flow is critical
    tech: 0.9,       // Tech is enabler
    people: 1.0,     // People are key
    ai: 0.8          // AI readiness is future-focused
  };
  
  let weightedSum = 0;
  let totalWeight = 0;
  
  dimensionScores.forEach(d => {
    const weight = weights[d.dimensionId] || 1.0;
    weightedSum += d.percentage * weight;
    totalWeight += weight;
  });
  
  return Math.round(weightedSum / totalWeight);
}

// Determine overall tier based on score
export function determineTier(score) {
  if (score <= 40) return 'Reactive';
  if (score <= 65) return 'Structured';
  if (score <= 85) return 'Optimised';
  return 'Autonomous';
}

// Get tier color
export function getTierColor(tier) {
  switch (tier) {
    case 'Reactive': return '#fc5c65';
    case 'Structured': return '#f7b731';
    case 'Optimised': return '#00d2a0';
    case 'Autonomous': return '#6c5ce7';
    default: return '#52525B';
  }
}

// Get tier summary text
export function getTierSummary(tier) {
  const summaries = {
    'Reactive': "Your organisation is operating in reactive mode — firefighting is the norm, not the exception. There are significant structural gaps that compound daily. The good news: organisations at this stage see the fastest ROI from targeted AI interventions.",
    'Structured': "Your organisation has solid foundations but is leaving significant capacity on the table. Key processes rely too heavily on individual heroics rather than systems. Targeted automation could unlock 20-30% more output from existing headcount.",
    'Optimised': "Your organisation is ahead of most — processes are intentional and leadership is aligned. The opportunity now is to move from good to exceptional by deploying AI to eliminate the remaining friction points and scale without proportional headcount growth.",
    'Autonomous': "Exceptional. Your organisation operates at a level most aspire to. The next frontier is predictive operations — using AI not just to automate, but to anticipate."
  };
  return summaries[tier] || '';
}

// Generate dimension feedback
export function getDimensionFeedback(dimensionScore) {
  const { name, percentage } = dimensionScore;
  
  if (percentage >= 70) {
    return `This is a strength. Your ${name} foundations are solid — protect this advantage as you scale.`;
  } else if (percentage >= 45) {
    return `Room to improve. Your ${name} is functional but creating hidden drag. Targeted changes could yield quick wins.`;
  } else {
    return `Critical gap. Your ${name} is a significant bottleneck that's likely compounding costs across the organisation.`;
  }
}

// Full scoring calculation
export function calculateFullResults(answers) {
  const dimensionScores = calculateDimensionScores(answers);
  const overallScore = calculateOverallScore(dimensionScores);
  const tier = determineTier(overallScore);
  const summary = getTierSummary(tier);
  
  return {
    overallScore,
    tier,
    summary,
    tierColor: getTierColor(tier),
    dimensionScores: dimensionScores.map(d => ({
      ...d,
      feedback: getDimensionFeedback(d)
    }))
  };
}
